import { useMemo } from "react";
import LocationScopeSearchableTree from "./LocationScopeSearchableTree";
import CheckboxTreeNodeDto from "../../../types/dtos/generic/CheckboxTreeNodeDto";
import { useTranslation } from "react-i18next";
import { BasicUserGroupDto } from "../../../types/admin/user-groups/BasicUserGroupDto";
import ScopeSearchableChecklist from "./ScopeSearchableChecklist";
import { flattenCheckboxTree } from "../../../helpers/searchableFilterChecklist";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolderUser,
  faBriefcase,
  faUsers,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";

interface AdvancedFilterModalProps {
  filterType: string;
  locationTreeChecked: string[];
  locationTreeExpanded: string[];
  onLocationTreeChecked(newState: string[]): void;
  onLocationTreeExpanded(newState: string[]): void;
  locations?: CheckboxTreeNodeDto[];
  isLocationsLoading: boolean;
  onClearSelections(filterType: string): void;
  appraisalLevels?: CheckboxTreeNodeDto[];
  isAppraisalLevelsLoading: boolean;
  onAppraisalLevelTreeChecked(newState: string[]): void;
  onAppraisalLevelTreeExpanded(newState: string[]): void;
  appraisalLevelTreeChecked: string[];
  appraisalLevelTreeExpanded: string[];
  jobTitles?: CheckboxTreeNodeDto[];
  isJobTitlesLoading: boolean;
  onJobTitleTreeChecked(newState: string[]): void;
  onJobTitleTreeExpanded(newState: string[]): void;
  jobTitleTreeChecked: string[];
  jobTitleTreeExpanded: string[];
  employeeStatuses?: CheckboxTreeNodeDto[];
  isEmployeeStatusesLoading?: boolean;
  onEmployeeStatusTreeChecked?(newState: string[]): void;
  onEmployeeStatusTreeExpanded?(newState: string[]): void;
  employeeStatusTreeChecked?: string[];
  employeeStatusTreeExpanded?: string[];
  userGroups?: BasicUserGroupDto[];
  isUserGroupsLoading?: boolean;
  onUserGroupTreeChecked?: (newState: string[]) => void;
  onUserGroupTreeExpanded?: (newState: string[]) => void;
  userGroupTreeChecked?: string[];
  userGroupTreeExpanded?: string[];
  /** Cap checklist/tree height (px); use a smaller value in chip popovers. */
  listMaxHeight?: number;
}

function AdvancedFilterModal({
  filterType,
  locationTreeChecked,
  locationTreeExpanded,
  onLocationTreeChecked,
  onLocationTreeExpanded,
  onClearSelections,
  locations,
  isLocationsLoading = false,
  appraisalLevels,
  isAppraisalLevelsLoading = false,
  onAppraisalLevelTreeChecked,
  onAppraisalLevelTreeExpanded,
  appraisalLevelTreeChecked,
  appraisalLevelTreeExpanded,
  jobTitles,
  isJobTitlesLoading = false,
  onJobTitleTreeChecked,
  onJobTitleTreeExpanded,
  jobTitleTreeChecked,
  jobTitleTreeExpanded,
  employeeStatuses,
  isEmployeeStatusesLoading = false,
  onEmployeeStatusTreeChecked,
  onEmployeeStatusTreeExpanded,
  employeeStatusTreeChecked,
  employeeStatusTreeExpanded,
  userGroups,
  isUserGroupsLoading = false,
  userGroupTreeChecked = [],
  userGroupTreeExpanded = [],
  onUserGroupTreeChecked = () => {},
  onUserGroupTreeExpanded = () => {},
  listMaxHeight,
}: AdvancedFilterModalProps) {
  const { t } = useTranslation();

  const hideTopClearAll =
    filterType === "JOB-TITLE" ||
    filterType === "APPRAISAL-LEVEL" ||
    filterType === "EMPLOYEE-STATUS" ||
    filterType === "USER-GROUP" ||
    filterType === "LOCATION";

  const jobTitleItems = useMemo(
    () => flattenCheckboxTree(jobTitles),
    [jobTitles]
  );
  const appraisalLevelItems = useMemo(
    () => flattenCheckboxTree(appraisalLevels),
    [appraisalLevels]
  );
  const employeeStatusItems = useMemo(
    () => flattenCheckboxTree(employeeStatuses),
    [employeeStatuses]
  );

  return (
    <div className="px-2">
      {!hideTopClearAll && (
        <div className="flex flex-row mb-4">
          <div className="grow">
            <button
              className="text-sm"
              onClick={() => onClearSelections(filterType)}
            >
              {t("Common.ClearAll")}
            </button>
          </div>
        </div>
      )}

      {filterType === "USER-GROUP" && (
        <ScopeSearchableChecklist
          items={
            userGroups?.map((g) => ({
              value: g.id.toString(),
              label: g.name,
            })) ?? []
          }
          checked={userGroupTreeChecked ?? []}
          onCheckedChange={(v) => onUserGroupTreeChecked?.(v)}
          itemLabelSingular="user group"
          itemLabelPlural="user groups"
          searchPlaceholder="Search user groups..."
          isLoading={isUserGroupsLoading ?? false}
          loadingMessage={t("Loading user groups") + "..."}
          listMaxHeight={listMaxHeight}
          itemIcon={
            <FontAwesomeIcon
              className="client-colour-icon"
              icon={faFolderUser}
            />
          }
        />
      )}

      {filterType === "LOCATION" && (
        <LocationScopeSearchableTree
          nodes={locations}
          checked={locationTreeChecked}
          expanded={locationTreeExpanded}
          onCheckedChange={onLocationTreeChecked}
          onExpandedChange={onLocationTreeExpanded}
          isLoading={isLocationsLoading}
          loadingMessage={t("Common.LoadingLocations") + "..."}
          listMaxHeight={listMaxHeight}
        />
      )}
      {filterType === "APPRAISAL-LEVEL" && (
        <ScopeSearchableChecklist
          items={appraisalLevelItems}
          checked={appraisalLevelTreeChecked}
          onCheckedChange={onAppraisalLevelTreeChecked}
          itemLabelSingular="level"
          itemLabelPlural="levels"
          searchPlaceholder="Search levels..."
          isLoading={isAppraisalLevelsLoading}
          loadingMessage={t("Loading levels") + "..."}
          listMaxHeight={listMaxHeight}
          itemIcon={
            <FontAwesomeIcon
              className="client-colour-icon"
              icon={faUsers}
            />
          }
        />
      )}
      {filterType === "JOB-TITLE" && (
        <ScopeSearchableChecklist
          items={jobTitleItems}
          checked={jobTitleTreeChecked}
          onCheckedChange={onJobTitleTreeChecked}
          itemLabelSingular="job title"
          itemLabelPlural="job titles"
          searchPlaceholder="Search job titles..."
          isLoading={isJobTitlesLoading}
          loadingMessage={t("Loading job titles") + "..."}
          listMaxHeight={listMaxHeight}
          itemIcon={
            <FontAwesomeIcon
              className="client-colour-icon"
              icon={faBriefcase}
            />
          }
        />
      )}
      {filterType === "EMPLOYEE-STATUS" && (
        <ScopeSearchableChecklist
          items={employeeStatusItems}
          checked={employeeStatusTreeChecked ?? []}
          onCheckedChange={(v) => onEmployeeStatusTreeChecked?.(v)}
          itemLabelSingular="status"
          itemLabelPlural="statuses"
          searchPlaceholder="Search statuses..."
          isLoading={isEmployeeStatusesLoading}
          loadingMessage={t("Loading statuses") + "..."}
          listMaxHeight={listMaxHeight}
          itemIcon={
            <FontAwesomeIcon
              className="client-colour-icon"
              icon={faUser}
            />
          }
        />
      )}
    </div>
  );
}

export default AdvancedFilterModal;
